import { useEffect, useRef, useState } from 'react';
import ArrowDown from '../../../assets/svg/ArrowDown';
import { WidthAndHeightSubProps } from '../../puck/reusable-props/widthAndHeight';
import { IAsset } from '../../interface/apiServiceInterface';
import { CarouselData } from '.';

export const CustomCarousel = ({
  carousels,
  interval,
  autoScroll,
  hideArrows,
  hideThumbnails,
  transition,
  customImage,
  isGlobalSize,
  thumbnailSize,
  thumbnailGap,
  arrowColor,
  arrowBackground,
  currentDeviceType,
  lazyLoading,
  editMode,
}: {
  carousels: CarouselData[];
  interval: number;
  autoScroll: boolean;
  hideArrows: boolean;
  hideThumbnails: boolean;
  isGlobalSize: boolean;
  transition: string;
  customImage: WidthAndHeightSubProps;
  thumbnailSize: WidthAndHeightSubProps;
  thumbnailGap: string;
  arrowColor: string;
  arrowBackground: {
    color: string;
    isInherit: boolean;
    hoverColor?: string;
  };
  currentDeviceType: string;
  lazyLoading: boolean;
  editMode?: boolean;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const length = carousels.length;

  const getImageUrl = (image: IAsset) =>
    image?.imageUrl
      ? `${process.env.REACT_APP_S3_BUCKET_URL}${image.imageUrl}`
      : 'https://t4.ftcdn.net/jpg/05/11/55/91/360_F_511559113_UTxNAE1EP40z1qZ8hIzGNrB0LwqwjruK.jpg';

  const nextSlide = () => {
    setCurrentIndex(prev => (prev + 1) % length);
  };

  const prevSlide = () => {
    setCurrentIndex(prev => (prev - 1 + length) % length);
  };

  useEffect(() => {
    if (!autoScroll || length <= 1) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      nextSlide();
    }, interval * 1000);

    return () => clearTimeout(timeoutRef.current!);
  }, [currentIndex, interval, length, autoScroll]);

  if (length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">No carousels added.</div>
    );
  }

  return (
    <div
      className="relative overflow-hidden w-full"
      style={{
        scrollbarColor: '#d4d4d8 transparent',
        scrollbarWidth: 'thin',
      }}>
      {/* Slides */}
      <div
        className={`flex transition-transform ${transition} ease-in-out justify-center`}
        style={{
          width: `${length * 100}%`,
          transform: `translateX(-${(100 / length) * currentIndex}%)`,
        }}>
        {carousels.map((testimonial, index) => {
          const imgWidth = isGlobalSize
            ? isNaN(+customImage.maxWidth[currentDeviceType])
              ? customImage.maxWidth[currentDeviceType]
              : +customImage.maxWidth[currentDeviceType]
            : isNaN(+testimonial.imageSize.maxWidth[currentDeviceType])
              ? testimonial.imageSize.maxWidth[currentDeviceType]
              : +testimonial.imageSize.maxWidth[currentDeviceType];
          const imgHeight = isGlobalSize
            ? isNaN(+customImage.height[currentDeviceType])
              ? customImage.height[currentDeviceType]
              : +customImage.height[currentDeviceType]
            : isNaN(+testimonial.imageSize.height[currentDeviceType])
              ? testimonial.imageSize.height[currentDeviceType]
              : +testimonial.imageSize.height[currentDeviceType];
          return (
            <div
              key={testimonial.id}
              className="w-full flex-shrink-0 flex items-center justify-center"
              style={{ width: `${100 / length}%` }}>
              <img
                width={testimonial.image?.width}
                height={testimonial.image?.height}
                style={{
                  maxWidth: imgWidth,
                  ...(isLoaded || editMode
                    ? { height: imgHeight }
                    : { maxHeight: imgHeight }),
                }}
                src={getImageUrl(testimonial.image)}
                alt={testimonial.id}
                className="rounded-md max-h-full max-w-full object-cover"
                {...(index === 0 ? { fetchPriority: 'high' } : {})}
                {...(lazyLoading
                  ? { loading: index === 0 ? 'eager' : 'lazy' }
                  : {})}
                decoding="async"
                onLoad={() => setIsLoaded(true)}
              />
            </div>
          );
        })}
      </div>

      {/* Navigation Arrows */}
      {length > 1 && !hideArrows && (
        <>
          <button
            type="button"
            onClick={prevSlide}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 hover:bg-black/50 rounded w-10 h-10 flex items-center justify-center z-10"
            style={{
              backgroundColor: arrowBackground.isInherit
                ? 'inherit'
                : arrowBackground.color,
              color: arrowColor,
            }}
            onMouseOver={e => {
              e.currentTarget.style.backgroundColor =
                arrowBackground?.hoverColor || arrowBackground.color;
            }}
            onMouseOut={e => {
              e.currentTarget.style.backgroundColor = arrowBackground.color;
            }}
            aria-label="Previous Slide">
            {/* <span className="text-xl font-bold">‹</span> */}
            <ArrowDown className="rotate-90" />
          </button>
          <button
            type="button"
            onClick={nextSlide}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 hover:bg-black/50 rounded w-10 h-10 flex items-center justify-center z-10"
            style={{
              backgroundColor: arrowBackground.isInherit
                ? 'inherit'
                : arrowBackground.color,
              color: arrowColor,
            }}
            onMouseOver={e => {
              e.currentTarget.style.backgroundColor =
                arrowBackground?.hoverColor || arrowBackground.color;
            }}
            onMouseOut={e => {
              e.currentTarget.style.backgroundColor = arrowBackground.color;
            }}
            aria-label="Next Slide">
            {/* <span className="text-xl font-bold">›</span> */}
            <ArrowDown className="-rotate-90" />
          </button>
        </>
      )}

      {/* Thumbnails */}
      {!hideThumbnails && (
        <div className="mt-4 max-w-full overflow-x-auto px-2">
          <div
            className={`flex w-fit min-w-full justify-center`}
            style={{
              gap: `${thumbnailGap}px`,
            }}>
            {carousels.map((testimonial, index) => (
              <button
                type="button"
                key={testimonial.id}
                onClick={() => setCurrentIndex(index)}
                className={`border-2 rounded overflow-hidden flex-shrink-0 ${
                  index === currentIndex ? 'border-blue-600' : 'border-gray-300'
                }`}
                style={{
                  maxWidth: isNaN(+thumbnailSize.maxWidth[currentDeviceType])
                    ? thumbnailSize.maxWidth[currentDeviceType]
                    : +thumbnailSize.maxWidth[currentDeviceType],

                  height: isNaN(+thumbnailSize.height[currentDeviceType])
                    ? thumbnailSize.height[currentDeviceType]
                    : +thumbnailSize.height[currentDeviceType],
                }}
                aria-label={`Go to slide ${index + 1}`}>
                <img
                  src={getImageUrl(testimonial?.thumbnailImage)}
                  alt={`thumbnail-${index}`}
                  className="object-cover w-full h-full"
                  decoding="async"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
